
import { Component, Emit, Prop, Vue } from 'vue-property-decorator';
import Popup from '@/components/utils/Popup.vue';
import Logo from '@/components/utils/Logo.vue';
import {
  User,
  AuthMode,
  LoginInfo,
  LoginProvider,
  SiteInfo,
  SiteStatus,
  GuestUserRequest,
  InvitedSpaceIdAndRoomId,
} from '@/utils/models';
import { v4 as uuid } from 'uuid';
import store from 'store';
import CheckBox from '@/components/utils/CheckBox.vue';
import ZawaOperationalLogin from '@/components/popup/common/login-popup-contents/ZawaOperationalLogin.vue';
import ZawatiralOperatinalLogin from './login-popup-contents/ZawatrialOperatinalLogin.vue';
import ZawaSuspended from './login-popup-contents/ZawaSuspended.vue';
import ZawatrialSuspended from './login-popup-contents/ZawatrialSuspended.vue';
import { TYPE } from 'vue-toastification/src/ts/constants';
import { AxiosError } from 'axios';

@Component({
  components: {
    Popup,
    Logo,
    CheckBox,
    ZawatiralOperatinalLogin,
    ZawaOperationalLogin,
    ZawaSuspended,
    ZawatrialSuspended,
  },
})
export default class LoginPopup extends Vue {
  @Prop() siteStatus?: SiteStatus;
  userId = '';
  name = '';
  email = '';
  password = '';
  mode: AuthMode = '';
  aadWindow: Window | null = null;
  isVisible = true;
  agreed = false;
  hideClose = false;
  get version(): string {
    return this.$store.state.version ?? 'version';
  }

  get siteInfo(): SiteInfo {
    console.log(this.$store.state);
    return this.$store.state.siteInfo;
  }
  get showAnnouncement(): boolean {
    return !!this.siteInfo?.showTMCAnnouncement && !this.hideClose;
  }

  get providers(): LoginProvider[] {
    let ap = this.$store.state.siteInfo?.authLoginProviders ?? [];
    let providers: LoginProvider[] = this.$store.state.providers?.filter((p: LoginProvider) => ap.includes(p.id)) || [];
    providers.sort((a, b) => a.sortOrder - b.sortOrder);
    return providers;
  }

  get isEmailFormValid(): boolean {
    return this.isValidEmail;
  }
  get isValidEmail(): boolean {
    let re =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(this.email);
  }
  mounted() {
    this.retrieveUserId();
  }
  created() {
    this.loginWithMagicLink();
  }
  private retrieveUserId(): void {
    let userId = store.get('userId');
    if (userId === undefined) {
      userId = uuid();
      store.set('userId', userId);
    }
    this.userId = userId;
    this.name = store.get('userName');
    this.email = store.get('email');
  }

  openAADLogin(providerId: string) {
    (window as any).onAuthorizedByAAD = (user: LoginInfo) => {
      this.authrized({
        id: user.id,
        tokenHash: user.tokenHash,
        authMode: 'auth-service',
        name: user.name,
        providerId,
      });
      this.aadWindow?.close();
    };
    let windowFeatures = 'menubar=no,scrollbars=yes,status=no';
    let path = providerId.replace('_', '-');
    this.aadWindow = window.open(`../zawa-data/${path}`, 'auth', windowFeatures);
  }
  onClickClose() {
    this.hideClose = true;
  }
  onClose() {
    this.cancel();
  }
  cancel() {
    this.mode = '';
  }
  loginWithProvider(providerId: string) {
    this.openAADLogin(providerId);
  }
  async loginAsGuest(email: string) {
    const guestRequest: GuestUserRequest = {
      email,
    };

    const toastId = this.$toast('送信中です...', {});

    try {
      await this.axios.post(`/zawa-data/guest/${this.userId}`, guestRequest);
      setTimeout(() => {
        //急に更新されたら変なので（見た目的に）、少し待ってから更新
        this.$toast.update(toastId, {
          content: 'ログイン用URLを発行しました。メールをご確認ください。',
          options: { type: TYPE.SUCCESS },
        });
      }, 1000);
    } catch (e) {
      const error = e as AxiosError<string>;
      console.log('Failed to login with Magic Link', error);
      this.$toast.update(toastId, {
        content: `ログイン用URLの発行に失敗しました。${error.response?.data}`,
        options: { type: TYPE.ERROR },
      });
    }
  }
  @Emit()
  authrized(user: LoginInfo) {}

  /**
   * MagicLinkによるログイン試行か検知
   */
  get isAttemptToLoginWithMagicLink() {
    const url = new URL(location.href);
    const token = url.searchParams.get('token');
    return !!token;
  }

  /**
   * 招待付きMagicLinkによるログイン試行か検知
   */

  get isAttemptToLoginWithInvitationMagicLink() {
    const url = new URL(location.href);
    const userId = url.searchParams.get('userId');
    const spaceId = url.searchParams.get('spaceId');
    const roomId = url.searchParams.get('roomId');
    return this.isAttemptToLoginWithMagicLink && !!spaceId && !!userId && !!roomId;
  }

  /**
   * MagicLinkログインか検知し、ログイン処理を行う
   */
  loginWithMagicLink() {
    const url = new URL(location.href);
    if (this.isAttemptToLoginWithInvitationMagicLink) {
      //招待付きMagicLinkログイン
      const token = url.searchParams.get('token')!;
      const userId = url.searchParams.get('userId')!;
      const spaceId = url.searchParams.get('spaceId')!;
      const roomId = url.searchParams.get('roomId')!;
      const spaceIdAndRoomId: InvitedSpaceIdAndRoomId = [spaceId, roomId];
      store.set('userId', userId); //バックエンドで生成されたuserIdをStoreにセット
      this.$store.commit('setInvitation', spaceIdAndRoomId);
      const loginInfo: LoginInfo = {
        id: userId,
        tokenHash: token,
        authMode: 'guest',
        loginWithMagicLink: true,
      };
      this.authrized(loginInfo);
    } else if (this.isAttemptToLoginWithMagicLink) {
      //通常MagicLinkログイン
      const token = url.searchParams.get('token')!;
      let userId = store.get('userId');
      if (userId === undefined) {
        userId = uuid();
        store.set('userId', userId);
      }
      const loginInfo: LoginInfo = {
        id: userId,
        tokenHash: token,
        authMode: 'guest',
        loginWithMagicLink: true,
      };
      this.authrized(loginInfo);
    }
    // URLからクエリパラメータを削除
    history.replaceState(null, '', url.pathname);
  }
}
